import { useTranslations } from 'next-intl';
import { FC, memo, useMemo, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';

import TimeIcon from '@/public/icons/schedule.svg';
import { useLimitedTimer } from '@/src/hooks/useLimitedTimer';

const styles = {
  tag: 'absolute top-0 right-0 px-1 h-4 flex items-center gap-[2px] text-xs bg-fill-yellowFade80 text-label-black rounded-bl-lg',
  tagRose: 'bg-fill-roseFade80 text-label-white',
  icon: 'w-3 h-3',
};

export const LimitedTimer: FC<{
  endDate: Date;
  setIsLeft30mins: (is30mins: boolean) => void;
  setIsTimerLoaded: (is30mins: boolean) => void;
}> = memo(({ endDate, setIsLeft30mins, setIsTimerLoaded }) => {
  const t = useTranslations('MenuPage');
  const timer = useLimitedTimer(endDate);

  const days = timer?.days ?? 0;
  const hours = timer?.hours ?? 0;
  const minutes = timer?.minutes ?? 0;
  const isLeft30mins = timer?.isLeft30mins ?? false;

  const timeLabel = useMemo(() => {
    if (!timer) {
      return '';
    }
    if (days === 0 && hours >= 1) {
      return t('hour', { hours });
    }
    if (days === 0 && hours === 0) {
      return t('minute', { minutes });
    }
    return t('day', { days });
  }, [timer, days, hours, minutes, t]);

  useEffect(() => {
    if (timer) {
      setIsLeft30mins(isLeft30mins);
      setIsTimerLoaded(true);
    }
  }, [isLeft30mins, setIsLeft30mins, setIsTimerLoaded, timer]);

  return (
    <span
      className={twMerge(
        styles.tag,
        isLeft30mins && styles.tagRose,
        !timer && 'bg-transparent',
      )}
    >
      <TimeIcon className={styles.icon} />
      {timeLabel}
    </span>
  );
});

LimitedTimer.displayName = 'LimitedTimer';
